import {Checkbox, Dropdown, IDropdownOption} from '@fluentui/react';
import * as React from 'react';
import {useCallback, useEffect, useState} from 'react';
import {CellProps, Column, HeaderProps, Row} from 'react-table';
import styled from 'styled-components';
import {api, ApiContext} from '../api';
import {ignoreEmpty} from '../components/Filter';
import {
  PARAM_KEY_ORDER,
  PARAM_KEY_PAGE,
  PARAM_KEY_SIZE,
  PARAM_KEY_SORT,
} from '../consts';
import {ButtonField} from '../fields/ButtonField';
import {CheckboxField} from '../fields/CheckboxField';
import {FlagField} from '../fields/FlagField';
import {Actions} from '../types/Action';
import {Field, Option} from '../types/Field';
import {FormValues} from '../types/Form';
import {Params} from '../types/Params';
import {AttachmentFiles, RelatedResources} from '../types/Resource';
import {ResourceDetails} from '../types/ResourceDetails';
import {ResourceList} from '../types/ResourceList';
import {Schema} from '../types/Schema';
import {Sorter} from '../types/Sorter';
import {SortOrder} from '../types/SortOrder';
import {TableTool} from '../types/Table';
import {TableColumn} from '../types/TableColumn';
import {TableFilter} from '../types/TableFilter';
import {array} from '../util';
import {widgetProps} from '../widgets/common';
import {DateListOutputWidget} from '../widgets/DateListWidget';
import {DateWidget} from '../widgets/DateWidget';
import {ImageOutputWidget} from '../widgets/ImageWidget';
import {makeStyleForDeleted} from '../widgets/item/Item';
import {MonthOutputWidget} from '../widgets/MonthWidget';
import {TimeOutputWidget} from '../widgets/TimeWidget';
import {ActionText} from './ActionText';
import {minus} from './set/set';
import {
  CHECKBOX_COLUMN_ID,
  FIRST_COLUMN_ID,
  headerDecorator,
  Table,
  TREE_ARROW_COLUMN_ID,
  TREE_PADDING_UNIT_IN_REM,
  TreeTable,
} from './table/Table';
import {makeRowStyle} from './style/style';
import {MaterialIcon} from '../icons/MaterialIcon';
import {getContaineeIds, getContainees, hasContainees} from './tree/containee';
import {textColor, textColorTheme} from '../styles';
import {OnChangeSorterFn} from './table/SorterChanger';

const Container = styled.div``;

export const rowPadding = '8px';

const Header = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: pre;
  padding: ${rowPadding};
`;

const Cell = styled.div`
  padding: ${rowPadding};
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
`;

const Text = styled.div`
  height: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  line-height: 1.5;
`;

const ImageCell = styled.div`
  padding: 2px;
  text-align: center;
`;

const Image = styled.img<{size: number}>`
  max-height: ${(props) => props.size}px;
  max-width: ${(props) => props.size}px;
`;

type OptionMap = {[value: string]: string};
type OptionMaps = {[fieldId: string]: OptionMap};
type FieldMap = {[fieldId: string]: Field};

export const DEFAULT_COLUMN_WIDTH = 150;

type columnsDecorator = (columns: StickyColumn[]) => StickyColumn[];

type Props = {
  resourceList: ResourceList;
  defaultWidth?: number;
  columns: TableColumn[];
  onSelect: (item: ResourceDetails) => void;
  selectedIds?: string[];
  expandLastColumn: boolean;
  onChangePageSize: (size: number) => void;
  onChangeCurrentPage: (index: number) => void;
  onChangeSorter: OnChangeSorterFn;
  currentSortOrder?: SortOrder;
  sorters?: Sorter[];
  filters?: TableFilter[];
  onChangeFilter?: (values: FormValues) => void;
  emptyMessage?: string;
  tableTools?: TableTool[];
  tableFarTools?: TableTool[];
  actions: Actions;
  columnsDecorator?: columnsDecorator;
  headerDecorator?: headerDecorator;
  tree?: boolean;
} & CheckboxProps;

export type CheckboxProps = {
  checkbox?: boolean;
  onCheck?: (ids: Set<string>, checked: boolean) => void;
  checkedIds?: Set<string>;
};

export type ResourceDetailsContainer = {
  id: string;
  __row_id__?: string;
  item: ResourceDetails;
  subRows?: ResourceDetailsContainer[];
};

export function ResourceDetailsTable(props: Props) {
  const fn = props.onSelect;
  const onSelectRow = useCallback(
    (rc: ResourceDetailsContainer) => {
      fn(rc.item);
    },
    [fn],
  );

  const {cols, setCols} = useVisibleColumns(props.columns);

  let columns = buildColumns(
    props,
    props.resourceList,
    cols,
    props.expandLastColumn,
    props.checkbox,
    props.checkedIds,
    props.onCheck,
    props.defaultWidth,
    props.tree,
  );

  if (props.columnsDecorator) {
    columns = props.columnsDecorator(columns);
  }

  const rows = buildRows(props.resourceList, props.tree);

  if (props.tree) {
    return (
      <Container>
        <TreeTable<ResourceDetailsContainer>
          {...props}
          columns={columns}
          rows={rows}
          onSelectRow={onSelectRow}
          count={props.resourceList.count}
          currentPage={props.resourceList.currentPage}
          pageSize={props.resourceList.pageSize}
          maxPage={props.resourceList.maxPage}
          allColumns={props.columns}
          currentColumns={cols}
          fields={props.resourceList.schema.fields}
          onChangeColumns={setCols}
          info={props.resourceList.info}
        />
      </Container>
    );
  }

  return (
    <Container>
      <Table<ResourceDetailsContainer>
        {...props}
        columns={columns}
        rows={rows}
        onSelectRow={onSelectRow}
        count={props.resourceList.count}
        currentPage={props.resourceList.currentPage}
        pageSize={props.resourceList.pageSize}
        maxPage={props.resourceList.maxPage}
        allColumns={props.columns}
        currentColumns={cols}
        fields={props.resourceList.schema.fields}
        onChangeColumns={setCols}
        info={props.resourceList.info}
      />
    </Container>
  );
}

type VisibleColumns = {
  cols: TableColumn[];
  setCols: (columns: TableColumn[]) => void;
};

function useVisibleColumns(columns: TableColumn[]): VisibleColumns {
  // If the schema of the list is different from the schema of the filter, props.columns may switch dynamically.
  // If this happens, the cols have to be reconfigured.
  // However, if it is simply judged by the identity of props.columns,
  // the cols will be reset every time when a user do filter,
  // even if the schema of the list and the schema of the filter are the same.
  // To avoid this, save the value of props.columns and do not update it
  // when the contents are the same as the value of previous props.columns.
  const [cols, setCols] = useState<TableColumn[]>(selectVisible(columns));
  const [propsColumns, setPropsColumns] = useState<TableColumn[]>(columns);

  useEffect(() => {
    if (!columnsAreEqual(propsColumns, columns)) {
      setCols(selectVisible(columns));
      setPropsColumns(columns);
    }
  }, [columns, propsColumns]);

  return {cols, setCols};
}

function columnsAreEqual(cols1: TableColumn[], cols2: TableColumn[]): boolean {
  if (cols1.length !== cols2.length) {
    return false;
  }

  for (let i = 0; i < cols1.length; i++) {
    const c1 = cols1[i];
    const c2 = cols2[i];

    if (
      c1.fieldId !== c2.fieldId ||
      c1.label !== c2.label ||
      c1.visible !== c2.visible
    ) {
      return false;
    }
  }

  return true;
}

function selectVisible(columns: TableColumn[]): TableColumn[] {
  return columns.filter((c) => c.visible);
}

function sort(field: Field) {}

function getFilter(
  filters: TableFilter[] | undefined,
  id: string,
): TableFilter | null {
  if (!filters) {
    return null;
  }

  for (let f of filters) {
    if (f.id === id) {
      return f;
    }
  }

  return null;
}

function renderHeaderCell(
  field: Field,
  col: TableColumn,
  headerProps: HeaderProps<ResourceDetailsContainer>,
  props: Props,
) {
  return (
    <HeaderCell
      col={col}
      field={field}
      filters={props.filters}
      params={props.resourceList.params}
      onChangeFilter={props.onChangeFilter}
    />
  );
}

type HeaderCellProps = {
  col: TableColumn;
  field: Field;
  filters?: TableFilter[];
  params: Params;
  onChangeFilter?: (values: FormValues) => void;
};

function HeaderCell(props: HeaderCellProps): JSX.Element | null {
  const label = props.col.label || props.field.label;
  const filter = props.col.filter || getFilter(props.filters, props.field.id);

  if (filter && filter.options && props.onChangeFilter) {
    return (
      <HeaderWithFilter
        label={label}
        filter={filter}
        onChangeFilter={props.onChangeFilter}
        values={array(props.params[filter.id])}
      />
    );
  }

  return (
    <Header
      onClick={() => {
        sort(props.field);
      }}
      title={label}>
      {label}
    </Header>
  );
}

type HeaderWithFilterProps = {
  label: string;
  filter: TableFilter;
  onChangeFilter: (values: FormValues) => void;
  values: string[];
};

const paramKeyFiltered = '__filtered__';
const paramValueFiltered = '1';

export function isFilteredTable(list: ResourceList): boolean {
  return list.params[paramKeyFiltered] === paramValueFiltered;
}

function HeaderWithFilter(props: HeaderWithFilterProps): JSX.Element {
  const options = buildOptions(props.filter.options, props.values);

  return (
    // Container elements to center Dropdown vertically and set its width to 100%.
    // TODO Replace these `div` with a better approach.
    <div style={{height: '100%', display: 'flex', alignItems: 'center'}}>
      <div style={{display: 'inline-block', width: '100%'}}>
        <Dropdown
          options={options}
          multiSelect={true}
          placeholder={props.label}
          onRenderTitle={(selectedOptions) => {
            return <div>{props.label}</div>;
          }}
          dropdownWidth={'auto'}
          onChange={(_, opt) => {
            if (opt) {
              const newValues = addOrRemove(
                props.values,
                String(opt.key),
                opt.selected,
              );
              props.onChangeFilter({
                [props.filter.id]: newValues,
                [paramKeyFiltered]: paramValueFiltered,
              });
            }
          }}
          styles={{
            title: {
              border: 'none',
              color: props.values.length > 0 ? textColorTheme : textColor,
            },
          }}
        />
      </div>
    </div>
  );
}

function addOrRemove(
  values: string[],
  value: string,
  add: boolean | undefined,
): string[] {
  if (!value) {
    return values;
  }

  if (add) {
    return values.concat(value);
  }

  return values.filter((v) => {
    return v !== value;
  });
}

function buildOptions(options: Option[], values: string[]): IDropdownOption[] {
  return options.map((o) => {
    return {
      key: o.value,
      text: o.label,
      selected: values.includes(o.value),
    };
  });
}

const FIELD_ID_BODY_VALUE_COMMON_STYLE = '__bv_common_style__';
const FIELD_ID_BODY_VALUE_STYLES = '__bv_styles__';

function renderValue(
  field: Field,
  options: OptionMaps,
  relatedResources: RelatedResources,
  cellProps: CellProps<ResourceDetailsContainer>,
  props: Props,
  column: TableColumn,
) {
  if (field.widget === 'button') {
    const value = cellProps.cell.value;
    const disabled = typeof value === 'boolean' && !value;

    return (
      <ButtonField
        schema={props.resourceList.schema}
        field={field}
        actions={props.actions}
        item={cellProps.row.original.item}
        disabled={disabled}
      />
    );
  }

  if (field.widget === 'flag') {
    return (
      <FlagField
        value={cellProps.cell.value}
        schema={props.resourceList.schema}
        field={field}
        actions={props.actions}
        item={cellProps.row.original.item}
      />
    );
  }

  const value = cellProps.cell.value;

  if (value === null || value === undefined) {
    return null;
  }

  const v = chooseVisibleValue(value, field, options, relatedResources);
  const style = {
    ...column.styles?.body,
    ...cellProps.row.original.item[FIELD_ID_BODY_VALUE_COMMON_STYLE],
    ...cellProps.row.original.item[FIELD_ID_BODY_VALUE_STYLES]?.[field.id],
  };

  if (!field) {
    return (
      <Text title={v} style={style}>
        {v}
      </Text>
    );
  }

  if (field.widget === 'time') {
    return <TimeOutputWidget value={v} style={style} />;
  }

  if (field.widget === 'month') {
    return <MonthOutputWidget value={v} style={style} />;
  }

  if (field.type === 'date') {
    return <DateWidget value={v} readOnly={true} style={style} />;
  }

  if (field.type === 'date_list') {
    return <DateListOutputWidget value={v} style={style} />;
  }

  if (field.widget === 'image') {
    return renderImage(field, value);
  }

  if (field.widget === 'checkbox') {
    return (
      <CheckboxField
        value={value}
        field={field}
        readOnly={true}
        style={style}
      />
    );
  }

  return (
    <ActionText
      field={field}
      schema={props.resourceList.schema}
      item={cellProps.row.original.item}
      actions={props.actions}
      value={v}
      style={style}
    />
  );
}

function renderCell(
  schema: Schema,
  field: Field,
  options: OptionMaps,
  relatedResources: RelatedResources,
  files: AttachmentFiles,
  cellProps: CellProps<ResourceDetailsContainer>,
  props: Props,
  column: TableColumn,
) {
  if (field.type === 'file') {
    return renderFile(schema, field, files, cellProps);
  }

  const value = renderValue(
    field,
    options,
    relatedResources,
    cellProps,
    props,
    column,
  );

  return <Cell style={makeStyle(cellProps.row.original.item)}>{value}</Cell>;
}

function makeStyle(item?: ResourceDetails) {
  return {
    ...makeStyleForDeleted(item && item.is_deleted),
    ...makeRowStyle(item || {}),
  };
}

export type StickyColumn = Column<ResourceDetailsContainer> & {
  sticky?: 'left' | 'right';
};

function buildColumns(
  props: Props,
  resourceList: ResourceList,
  columns: TableColumn[],
  expandLastColumn: boolean,
  checkbox?: boolean,
  checkedIds?: Set<string>,
  onCheck?: (ids: Set<string>, checked: boolean) => void,
  defaultColumnWidth?: number,
  tree?: boolean,
): StickyColumn[] {
  const cs: Column<ResourceDetailsContainer>[] = [];

  if (tree && hasContainees(resourceList)) {
    cs.push(buildTreeArrowColumn());
  }

  if (checkbox && onCheck && checkedIds) {
    cs.push(buildCheckboxColumn(checkedIds, onCheck));
  }

  const schema = resourceList.schema;
  const fields = buildFieldMap(schema.fields);
  const options = buildOptionMaps(resourceList.schema.fields);

  let hasExpandableColumn = false;

  let first = true;

  for (let col of columns) {
    const field = fields[col.fieldId];

    if (!field) {
      continue;
    }

    if (col.minWidth) {
      hasExpandableColumn = true;
    }

    const id = first ? FIRST_COLUMN_ID : field.id;
    first = false;

    cs.push(
      buildFieldColumn(
        id,
        field,
        col,
        props,
        resourceList,
        options,
        defaultColumnWidth,
      ),
    );
  }

  if (!hasExpandableColumn && !expandLastColumn) {
    cs.push(styleOverridableNullColumn);
  }

  return cs;
}

export const resourceDetailsTableNullColumn: Column<ResourceDetailsContainer> =
  {
    Header: () => {
      return null;
    },
    Cell: () => {
      return null;
    },
    id: '__null_column__',
    width: 0,
    minWidth: -1,
  };

const styleOverridableNullColumn: Column<ResourceDetailsContainer> = {
  ...resourceDetailsTableNullColumn,
  Cell: (cellProps: CellProps<ResourceDetailsContainer>) => {
    // Return an empty cell with the style specified by __style__ property to apply the background color to the entire row.
    const style = makeStyle(cellProps.row.original.item);
    return <Cell style={style} />;
  },
};

function buildCheckboxColumn(
  checkedIds: Set<string>,
  onCheck: (ids: Set<string>, checked: boolean) => void,
): Column<ResourceDetailsContainer> {
  return {
    Header: (headerProps: HeaderProps<ResourceDetailsContainer>) => {
      const ids = getAllIds(headerProps.rows);
      const checked = ids.size > 0 && minus(ids, checkedIds).size === 0;

      return (
        <RowCheckbox
          onCheck={(checked: boolean) => {
            onCheck(ids, checked);
          }}
          checked={checked}
        />
      );
    },
    Cell: (cellProps: CellProps<ResourceDetailsContainer>) => {
      const id = cellProps.row.id;
      const checked = checkedIds.has(id);
      const ids = new Set<string>([
        id,
        // Checking a container will also check its containees.
        ...getContaineeIds(cellProps.row.original.item),
      ]);

      return (
        <RowCheckbox
          onCheck={(checked: boolean) => {
            onCheck(ids, checked);
          }}
          checked={checked}
        />
      );
    },
    width: 40,
    id: CHECKBOX_COLUMN_ID,
    accessor: (originalRow: ResourceDetailsContainer) => {
      return originalRow.item['__checkbox__'];
    },
  };
}

function getAllIds(rows: Row<ResourceDetailsContainer>[]): Set<string> {
  const ids = new Set<string>();

  for (let row of rows) {
    ids.add(row.id);

    for (let id of getContaineeIds(row.original.item)) {
      ids.add(id);
    }
  }

  return ids;
}

function buildTreeArrowColumn(): Column<ResourceDetailsContainer> {
  return {
    id: TREE_ARROW_COLUMN_ID,
    Header: (headerProps: HeaderProps<ResourceDetailsContainer>) => {
      return <div style={{width: TREE_PADDING_UNIT_IN_REM + 'rem'}} />;
    },
    Cell: (cellProps: CellProps<ResourceDetailsContainer>) => {
      const row = cellProps.row as any;
      const width = `${(row.depth + 1) * TREE_PADDING_UNIT_IN_REM}rem`;
      const eProps = row.getToggleRowExpandedProps({
        style: {width, fontSize: 24},
      });

      let onClick = (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
        eProps.onClick();
      };

      return row.canExpand ? (
        <TreeArrow {...eProps} onClick={onClick}>
          {row.isExpanded ? (
            <MaterialIcon iconName={'keyboard_arrow_down'} />
          ) : (
            <MaterialIcon iconName={'keyboard_arrow_right'} />
          )}
        </TreeArrow>
      ) : (
        <div style={{width}} />
      );
    },
    width: 'auto',
  };
}

const TreeArrow = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

function buildFieldColumn(
  id: string,
  field: Field,
  col: TableColumn,
  props: Props,
  resourceList: ResourceList,
  options: OptionMaps,
  defaultColumnWidth?: number,
): Column<ResourceDetailsContainer> {
  const schema = resourceList.schema;
  const relatedResources = resourceList.relatedResources || {};
  const files = resourceList.attachmentFiles || {};

  const c: Column<ResourceDetailsContainer> = {
    Header: (headerProps: HeaderProps<ResourceDetailsContainer>) => {
      return renderHeaderCell(field, col, headerProps, props);
    },
    Cell: (cellProps: CellProps<ResourceDetailsContainer>) => {
      return renderCell(
        schema,
        field,
        options,
        relatedResources,
        files,
        cellProps,
        props,
        col,
      );
    },
    id: id,
    accessor: (originalRow: ResourceDetailsContainer) => {
      return originalRow.item[field.id];
    },
  };

  if (col.minWidth) {
    c.minWidth = col.minWidth;
  } else {
    c.width = col.width || defaultColumnWidth || DEFAULT_COLUMN_WIDTH;
  }

  return c;
}

function buildFieldMap(fields: Field[]): FieldMap {
  const map: FieldMap = {};

  for (let field of fields) {
    map[field.id] = field;
  }

  return map;
}

function buildOptionMaps(fields: Field[]): OptionMaps {
  const options: OptionMaps = {};

  for (let field of fields) {
    if (field.options) {
      const opts: OptionMap = {};

      for (let o of field.options) {
        opts[o.value] = o.label;
      }

      options[field.id] = opts;
    }
  }

  return options;
}

function buildRows(
  resourceList: ResourceList,
  tree?: boolean,
): ResourceDetailsContainer[] {
  if (tree) {
    return convertToTreeRows(resourceList.list);
  }

  return resourceList.list.map((item) => ({
    id: item.id,
    __row_id__: item.__row_id__,
    item: item,
  }));
}

function convertToTreeRows(
  items: ResourceDetails[],
): ResourceDetailsContainer[] {
  const result: ResourceDetailsContainer[] = [];

  for (let item of items) {
    result.push({
      id: item.id,
      __row_id__: item.__row_id__,
      item: item,
      subRows: convertToTreeRows(getContainees(item)),
    });
  }

  return result;
}

function chooseVisibleValue(
  value: any,
  field: Field,
  options: OptionMaps,
  relatedResources: RelatedResources,
): any {
  if (Array.isArray(value)) {
    const values = array(value);
    return values.map((v) =>
      chooseVisibleValue(v, field, options, relatedResources),
    );
  }

  if (field.type === 'resid' || field.type === 'resid_list') {
    if (relatedResources[value]) {
      const propsFid = widgetProps<string>(field, 'name_field_id', '');

      for (let fid of [propsFid, '__name__', 'name']) {
        if (fid && relatedResources[value][fid]) {
          return relatedResources[value][fid];
        }
      }

      return '';
    }

    return '';
  }

  if (options[field.id]) {
    return options[field.id][value];
  }

  return String(value);
}

function renderFile(
  schema: Schema,
  field: Field,
  files: AttachmentFiles,
  cellProps: CellProps<ResourceDetailsContainer>,
) {
  const value = cellProps.cell.value;

  if (!value) {
    return null;
  }

  const column = cellProps.column;
  return mainImage(value, column, schema, field, files);
}

function mainImage(
  value: any,
  column: Column<ResourceDetailsContainer>,
  schema: Schema,
  field: Field,
  files: AttachmentFiles,
) {
  for (let v of array(value)) {
    const file = files[v];

    if (!file) {
      continue;
    }

    if (/^image/.test(file.contentType)) {
      const url = `/app/${schema.id}/${file.resourceId}/file/${file.id}`;
      return (
        <ImageCell>
          <a href={url} target="_blank" rel="noopener noreferrer">
            <Image
              key={`file-${file.id}`}
              src={url}
              size={parseInt(String(column.width), 10) - 20}
            />
          </a>
        </ImageCell>
      );
    }
  }

  return null;
}

type ResourceListSetter = (list: ResourceList) => void;

const reloadList = async (
  ctx: ApiContext,
  newParams: {[key: string]: any},
  setter: ResourceListSetter,
  list?: ResourceList,
  defaultParams?: Params,
) => {
  if (!list) {
    return;
  }

  const params: Params = list.params || {};

  Object.keys(newParams).forEach((k) => {
    params[k] = newParams[k];
  });

  const validParams = ignoreEmpty(params, Object.keys(defaultParams || {}));

  const resourceList = await api.list(ctx, list.schema.id, validParams);
  setter(resourceList);
};

async function onChangeCurrentPage(
  ctx: ApiContext,
  page: number,
  setter: ResourceListSetter,
  list?: ResourceList,
  defaultParams?: Params,
) {
  await reloadList(
    ctx,
    {
      [PARAM_KEY_PAGE]: page,
    },
    setter,
    list,
    defaultParams,
  );
}

async function onChangePageSize(
  ctx: ApiContext,
  size: number,
  setter: ResourceListSetter,
  list?: ResourceList,
  defaultParams?: Params,
) {
  await reloadList(
    ctx,
    {
      [PARAM_KEY_SIZE]: size,
      [PARAM_KEY_PAGE]: 0, // reset page select
    },
    setter,
    list,
    defaultParams,
  );
}

async function onChangeSorter(
  ctx: ApiContext,
  sorter: Sorter,
  current: SortOrder,
  setter: ResourceListSetter,
  list?: ResourceList,
  defaultParams?: Params,
) {
  const order =
    current.sort === sorter.id
      ? current.order === 'desc'
        ? 'asc'
        : 'desc'
      : sorter.defaultOrder;

  await reloadList(
    ctx,
    {
      [PARAM_KEY_SORT]: sorter.id,
      [PARAM_KEY_ORDER]: order,
    },
    setter,
    list,
    defaultParams,
  );
}

async function onChangeFilter(
  ctx: ApiContext,
  values: FormValues,
  setter: ResourceListSetter,
  list?: ResourceList,
  defaultParams?: Params,
) {
  await reloadList(ctx, values, setter, list, defaultParams);
}

type PagerProps = {
  onChangeCurrentPage: (page: number) => void;
  onChangePageSize: (size: number) => void;
  onChangeSorter: OnChangeSorterFn;
  onChangeFilter: (values: FormValues) => void;
};

export function buildPagerProps(
  ctx: ApiContext,
  setter: ResourceListSetter,
  list?: ResourceList,
  defaultParams?: Params,
): PagerProps {
  return {
    onChangeCurrentPage: async (page: number) => {
      await onChangeCurrentPage(ctx, page, setter, list, defaultParams);
    },
    onChangePageSize: async (size: number) => {
      await onChangePageSize(ctx, size, setter, list, defaultParams);
    },
    onChangeSorter: async (sorter: Sorter, current: SortOrder) => {
      await onChangeSorter(ctx, sorter, current, setter, list, defaultParams);
    },
    onChangeFilter: async (values: FormValues) => {
      await onChangeFilter(ctx, values, setter, list, defaultParams);
    },
  };
}

export function buildEmptyPagerProps(): PagerProps {
  return {
    onChangeCurrentPage: (page: number) => {},
    onChangePageSize: (size: number) => {},
    onChangeSorter: (sorter: Sorter, current: SortOrder) => {},
    onChangeFilter: (values: FormValues) => {},
  };
}

function renderImage(field: Field, value: any) {
  return (
    <ImageOutputWidget
      value={value}
      alt={field.label}
      size={80}
      styles={{
        root: {margin: '0'},
        img: {borderWidth: '2px'},
        notFound: {borderWidth: '2px'},
      }}
    />
  );
}

type RowCheckboxProps = {
  checked: boolean;
  onCheck: (checked: boolean) => void;
};

function RowCheckbox(props: RowCheckboxProps): JSX.Element | null {
  return (
    <CheckboxContainer
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        props.onCheck(!props.checked);
      }}>
      <Checkbox checked={props.checked} />
    </CheckboxContainer>
  );
}

const CheckboxContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export function productSet(list: ResourceList, ids: Set<string>): Set<string> {
  if (ids.size === 0) {
    return ids;
  }

  const result = new Set<string>();

  for (let details of list.list) {
    if (ids.has(details.id)) {
      result.add(details.id);
    }
  }

  return result;
}
